import { useEffect, useRef, type RefObject } from 'react';
import type { AudioAnalyserService } from '../services/audio-analyser.service';
import type { VoiceModeState } from '../services/voice-mode-orchestrator';

const IDLE_SPEED = 0.003;
const IDLE_AMPLITUDE = 0.35;
const IDLE_BASE = 0.15;

/**
 * Drives waveform bar heights via direct DOM writes (no React re-renders).
 * Runs at the display's native refresh rate for smooth animation.
 */
export function useWaveformAnimation(
  containerRef: RefObject<HTMLDivElement | null>,
  analyserService: AudioAnalyserService | null,
  voiceModeState: VoiceModeState,
  barCount: number,
  minHeightPx: number,
  maxHeightPx: number
): void {
  const rafIdRef = useRef<number>(0);
  const bufferRef = useRef<number[]>(new Array(barCount).fill(0));

  // biome-ignore lint/correctness/useExhaustiveDependencies: containerRef is a stable ref - reading .current inside the effect is correct
  useEffect(() => {
    bufferRef.current = new Array(barCount).fill(0);

    if (voiceModeState === 'off') {
      const bars = containerRef.current?.children;
      if (bars) {
        for (let i = 0; i < bars.length; i++) {
          (bars[i] as HTMLElement).style.height = `${minHeightPx * 2}px`;
        }
      }
      return;
    }

    const isRealAudio = voiceModeState === 'listening';

    const tick = (timestamp: number) => {
      const container = containerRef.current;
      if (!container) {
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const buf = bufferRef.current;

      if (isRealAudio && analyserService) {
        const amplitude = analyserService.getAmplitude();
        for (let i = 0; i < buf.length - 1; i++) {
          buf[i] = buf[i + 1];
        }
        buf[buf.length - 1] = amplitude;
      } else if (voiceModeState === 'speaking') {
        // Flat at zero - same rendering path as listening but silent
        buf.fill(0);
      } else {
        for (let i = 0; i < barCount; i++) {
          const phase = (i / barCount) * Math.PI * 2;
          const wave = Math.sin(timestamp * IDLE_SPEED + phase);
          buf[i] = IDLE_BASE + ((wave + 1) / 2) * IDLE_AMPLITUDE;
        }
      }

      const bars = container.children;
      for (let i = 0; i < bars.length; i++) {
        const level = buf[i] ?? 0;
        const halfHeight = minHeightPx + level * (maxHeightPx - minHeightPx);
        (bars[i] as HTMLElement).style.height = `${halfHeight * 2}px`;
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [voiceModeState, analyserService, barCount, minHeightPx, maxHeightPx]);
}
